import { NavLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, Package, BarChart3, User } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { useT } from '@/i18n';
import { haptic } from '@/shared/lib/telegram';

// Each tab carries its own accent so the active-state glow matches the
// section's visual identity (catalog = sky, orders = amber, etc).
const items: { to: string; key: string; Icon: LucideIcon; accent: string }[] = [
  { to: '/home', key: 'nav.home', Icon: Home, accent: '#2AABEE' },
  { to: '/orders', key: 'nav.orders', Icon: Package, accent: '#F59E0B' },
  { to: '/stats', key: 'nav.stats', Icon: BarChart3, accent: '#22C55E' },
  { to: '/profile', key: 'nav.profile', Icon: User, accent: '#D4537E' },
];

export function BottomNav() {
  const t = useT();
  // The Stats tab/menu always stays visible — only the numeric values inside
  // the page are masked for restricted users (handled in StatsPage).
  const visibleItems = items;
  return (
    <nav
      className="fixed bottom-0 inset-x-0 z-30 backdrop-blur-2xl"
      style={{
        // Layered padding so icons sit clear of:
        //   - the home-indicator / gesture zone (--safe-bottom, floored at 20)
        //   - and have visual breathing room from the bar edge (+10 px)
        paddingBottom: 'calc(var(--safe-bottom) + 10px)',
        paddingTop: '8px',
        // Layered look: solid base + frosted top edge for depth, no harsh line.
        background:
          'linear-gradient(180deg, rgba(15,15,15,0.78) 0%, rgba(15,15,15,0.96) 60%)',
        boxShadow:
          '0 -20px 60px -10px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.06)',
      }}
    >
      {/* Hairline accent at the top — barely visible, just enough to separate */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-px"
        style={{
          background:
            'linear-gradient(90deg, transparent, rgba(255,255,255,0.18), transparent)',
        }}
      />
      <ul
        className={cn(
          'grid gap-1 px-3 pt-2',
          visibleItems.length === 3 ? 'grid-cols-3' : 'grid-cols-4',
        )}
      >
        {visibleItems.map((it) => (
          <li key={it.to} className="relative">
            <NavLink
              to={it.to}
              onClick={() => haptic('selection')}
              // Inner padding so the icon + label have room to breathe and never
              // hug the cell's corners. py-2.5 keeps the touch target ≥44 px.
              className="flex flex-col items-center justify-center gap-1.5 px-2 py-2.5 rounded-2xl select-none"
            >
              {({ isActive }) => (
                <>
                  {/* Active pill: animated shared layout (slides between tabs).
                      inset-1 keeps a 4 px gap between pill and cell edges so
                      the glow blends into the bar instead of clipping. */}
                  {isActive && (
                    <motion.span
                      layoutId="bottomnav-active-pill"
                      transition={{ type: 'spring', stiffness: 420, damping: 32 }}
                      className="pointer-events-none absolute inset-1 rounded-2xl"
                      style={{
                        background: `radial-gradient(120% 100% at 50% 0%, ${it.accent}33, transparent 72%)`,
                        boxShadow: `0 0 22px ${it.accent}55, inset 0 0 0 1px ${it.accent}33`,
                      }}
                    />
                  )}
                  {/* Active dot above icon — appears with a tiny spring */}
                  {isActive && (
                    <motion.span
                      layoutId="bottomnav-active-dot"
                      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                      className="absolute top-0 h-1 w-1 rounded-full"
                      style={{
                        background: it.accent,
                        boxShadow: `0 0 8px ${it.accent}, 0 0 16px ${it.accent}aa`,
                      }}
                    />
                  )}
                  <motion.span
                    animate={{
                      scale: isActive ? 1.08 : 1,
                      y: isActive ? -1 : 0,
                    }}
                    transition={{ type: 'spring', stiffness: 480, damping: 24 }}
                    className="relative"
                    style={{
                      color: isActive ? '#FFFFFF' : 'rgba(255,255,255,0.55)',
                      filter: isActive
                        ? `drop-shadow(0 0 8px ${it.accent}cc) drop-shadow(0 0 16px ${it.accent}88)`
                        : 'none',
                      transition: 'filter 220ms ease, color 220ms ease',
                    }}
                  >
                    <it.Icon
                      size={22}
                      strokeWidth={isActive ? 2.4 : 1.8}
                      absoluteStrokeWidth
                    />
                  </motion.span>
                  <span
                    className={cn(
                      'relative text-[10.5px] font-medium leading-none transition-colors',
                      isActive ? 'text-white' : 'text-white/55',
                    )}
                  >
                    {t(it.key)}
                  </span>
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
